import { createReducer, on } from '@ngrx/store';
import {
  Workflow,
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepRule,
  WorkflowStepRuleAction,
  View,
  ViewControlType,
  WorkflowRuleImplementation,
  ViewNames,
  WorkflowStepTypes,
  WorkflowStatus
} from 'src/app/modules/core/store/models/workflow.model';
import * as WorkflowActions from './workflow.actions';
import { v4 as uuidv4 } from 'uuid';
import produce from 'immer';

export const workflowFeatureKey = 'communicationWorkflow';

export interface State {
  selectedWorkflow?: Workflow | null;
  selectedWorkflowStep?: WorkflowStep | null;
  selectedWorkflowStepAction?: WorkflowStepAction | null;
  selectedWorkflowStepForDelete?: WorkflowStep | null;
  selectedWorkflowStepActionForDelete?: WorkflowStepAction | null;
  selectedViewForDelete?: View | null;
  workflowStepsAfterDelete?: WorkflowStep[];
  stepMigrationUId: string;
  oldStepMigrationUId: string;

  selectedWorkflowStepRule?: WorkflowStepRule | null;
  selectedWorkflowStepRuleForDelete?: WorkflowStepRule | null;
  ruleImplementations?: WorkflowRuleImplementation[];

  workflowStatuses: WorkflowStatus[];

  err?: any;
  ui?: {
    isDirty?: boolean;
    isStepMigration?: boolean;
    isWorking?: boolean;
    showFormBuilder?: boolean;
    isTransitionFormNew?: boolean;
    isTableChanged?: boolean;

    isNewRule?: boolean;
    isNewAction?: boolean;
  };
}

export const initialState: State = {
  selectedWorkflow: null,
  selectedWorkflowStep: null,
  selectedWorkflowStepAction: null,
  selectedWorkflowStepForDelete: null,
  selectedWorkflowStepActionForDelete: null,
  selectedViewForDelete: null,
  workflowStepsAfterDelete: [],
  stepMigrationUId: null,
  oldStepMigrationUId: null,

  workflowStatuses: [],

  selectedWorkflowStepRule: null,
  selectedWorkflowStepRuleForDelete: null,
  ruleImplementations: [],

  err: null,
  ui: {
    isDirty: false,
    isStepMigration: false,
    isWorking: false,
    showFormBuilder: false,
    isTransitionFormNew: false,
    isTableChanged: false,

    isNewRule: false,
    isNewAction: false
  }
};

export const reducer = createReducer(
  initialState,

  on(WorkflowActions.setSelectedWorkflow, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflow: action.workflow,
      selectedWorkflowStep: null,
      selectedWorkflowStepAction: null,
      selectedView: null,
      selectedWorkflowStepForDelete: null,
      selectedWorkflowStepActionForDelete: null,
      selectedViewForDelete: null,
      selectedWorkflowStepRule: null,
      ui: {
        ...state.ui,
        isWorking: false,
        isDirty: false,
        showFormBuilder: false,
        isTransitionFormNew: false
      }
    };
  }),
  on(WorkflowActions.getWorkflowForDatasetComplete, (state, action) => {
    let selectedWorkflow = action.workflow;

    const newViews = [];
    const detailView = selectedWorkflow?.views?.find(vw => vw.name === 'detail');
    if (!detailView) {
      newViews.push({
        uId: uuidv4(),
        name: 'detail',
        description: null,
        controls: [],
        configuration: {}
      });
    }
    const tableView = selectedWorkflow?.views?.find(vw => vw.name === 'table');
    if (!tableView) {
      newViews.push({
        uId: uuidv4(),
        name: 'table',
        description: null,
        controls: [],
        configuration: {}
      });
    }

    const cardView = selectedWorkflow?.views?.find(vw => vw.name === 'card');
    if (!cardView) {
      newViews.push({
        uId: uuidv4(),
        name: 'card',
        description: null,
        controls: [],
        configuration: {}
      });
    }

    if (newViews.length > 0) {
      selectedWorkflow = produce(selectedWorkflow, draftState => {
        if (!!draftState) {
          draftState.views.push(...newViews);
        }
      });
    }

    return {
      ...state,
      err: null,
      selectedWorkflow,
      selectedWorkflowStep: null,
      selectedWorkflowStepAction: null,
      selectedWorkflowStepForDelete: null,
      selectedWorkflowStepActionForDelete: null,
      selectedViewForDelete: null,
      selectedWorkflowStepRule: null,
      ui: {
        ...state.ui,
        isWorking: false,
        isDirty: newViews.length > 0 ? true : false,
        showFormBuilder: false,
        isTransitionFormNew: false
      }
    };
  }),
  on(WorkflowActions.setWorkflowStepForDelete, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflowStepForDelete: action.workflowStep
    };
  }),
  on(WorkflowActions.clearWorkflowStepForDelete, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflowStepForDelete: null,
      workflowStepsAfterDelete: null
    };
  }),
  on(WorkflowActions.setWorkflowStepsForDeleteComplete, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflowStepForDelete: action.deleteStep,
      workflowStepsAfterDelete: action.results
    };
  }),
  on(WorkflowActions.setWorkflowActionForDelete, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflowStep: action.workflowStep,
      selectedWorkflowStepActionForDelete: action.action
    };
  }),
  on(WorkflowActions.clearWorkflowStepActionForDelete, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflowStepActionForDelete: null
    };
  }),
  on(WorkflowActions.deleteWorkflowStep, (state, action) => {
    const { selectedWorkflow, selectedWorkflowStepForDelete } = state;

    const stepIndexToRemove = selectedWorkflow.workflowSteps.findIndex(ws => ws.uId === selectedWorkflowStepForDelete.uId);

    const newWorkflow = produce(selectedWorkflow, draftState => {
      draftState.workflowSteps.splice(stepIndexToRemove, 1);
      for (const step of draftState.workflowSteps) {
        if (step.workflowStepType !== WorkflowStepTypes.Approval) {
          const newActions = step?.actions?.filter(a => a.destinationStepUId !== selectedWorkflowStepForDelete.uId);
          step.actions = newActions;
        } else {
          step?.actions
            ?.filter(a => a.destinationStepUId === selectedWorkflowStepForDelete.uId)
            ?.forEach(a => (a.destinationStepUId = null));
        }
      }
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      selectedWorkflowStepForDelete: null,
      workflowStepsAfterDelete: null,
      ui: {
        ...state.ui,
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.deleteWorkflowStepAction, (state, action) => {
    const { selectedWorkflow, selectedWorkflowStep, selectedWorkflowStepActionForDelete } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(s => s.uId === selectedWorkflowStep.uId);
      const actionToRemove = step.actions.findIndex(a => a.uId === selectedWorkflowStepActionForDelete.uId);
      step.actions.splice(actionToRemove, 1);
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      selectedWorkflowStepActionForDelete: null,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.updateWorkflowStepAction, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(s => s.uId === action.workflowStep.uId);
      const actionIndex = step.actions.findIndex(a => a.uId === action.stepAction.uId);
      step.actions[actionIndex] = action.stepAction;
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.updateWorkflowStep, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const stepIndex = draftState.workflowSteps.findIndex(ws => ws.uId === action.workflowStep.uId);
      draftState.workflowSteps[stepIndex] = action.workflowStep;
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.addWorkflowStepAction, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(ws => ws.uId === action.workflowStep.uId);

      const newAction: WorkflowStepAction = {
        uId: uuidv4(),
        sortOrder: step.actions.length + 1,
        name: '',
        description: '',
        iconName: '',
        destinationStepUId: null,
        transitionViewUId: null
      };
      step.actions.push(newAction);
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.updateWorkflowComplete, (state, action) => {
    return {
      ...state,
      ui: {
        isWorking: false,
        isDirty: false,
        isStepMigration: false
      }
    };
  }),
  on(WorkflowActions.addWorkflowStep, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      draftState.workflowSteps.push({
        uId: uuidv4(),
        sortOrder: draftState.workflowSteps.length + 1,
        name: '',
        workflowStepType: WorkflowStepTypes.Standard,
        description: '',
        workflowStatusUId: null,
        statusName: null,
        statusHtmlColorCode: null,
        actions: [],
        rules: [],
        operations: [],
        configuration: { firstStep: false, lastStep: false }
      });
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.moveWorkflowStep, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const [stepToMove] = draftState.workflowSteps.splice(action.previousIndex, 1);
      draftState.workflowSteps.splice(action.currentIndex, 0, stepToMove);

      let sortOrder = 1;
      for (const stepToRenumber of draftState.workflowSteps) {
        stepToRenumber.sortOrder = sortOrder;
        sortOrder++;
      }
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.moveWorkflowStepAction, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(ws => ws.uId === action.workflowStep.uId);
      const [actionToMove] = step.actions.splice(action.previousIndex, 1);
      step.actions.splice(action.currentIndex, 0, actionToMove);

      let sortOrder = 1;
      for (const actionToRenumber of step.actions) {
        actionToRenumber.sortOrder = sortOrder;
        sortOrder++;
      }
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.setIsDirty, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isDirty: action.isDirty
      }
    };
  }),

  // Rules
  on(WorkflowActions.setSelectedWorkflowStepRule, (state, action) => {
    return {
      ...state,
      selectedWorkflowStep: action.step,
      selectedWorkflowStepRule: action.rule
    };
  }),

  on(WorkflowActions.clearSelectedWorkflowStepRule, (state, action) => {
    return {
      ...state,
      selectedWorkflowStepRule: null
    };
  }),

  on(WorkflowActions.loadWorkflowRuleImplementationsComplete, (state, action) => {
    return {
      ...state,
      ruleImplementations: action.ruleImplementations
    };
  }),

  on(WorkflowActions.setWorkflowStepRuleForDelete, (state, action) => {
    return {
      ...state,
      selectedWorkflowStep: action.step,
      selectedWorkflowStepRuleForDelete: action.rule
    };
  }),

  on(WorkflowActions.clearWorkflowStepRuleForDelete, (state, action) => {
    return {
      ...state,
      selectedWorkflowStepRuleForDelete: null
    };
  }),

  on(WorkflowActions.moveWorkflowStepRule, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(ws => ws.uId === action.workflowStep.uId);
      const [ruleToMove] = step.rules.splice(action.previousIndex, 1);
      step.rules.splice(action.currentIndex, 0, ruleToMove);

      let sortOrder = 0;
      step.rules.map(r => (r.sortOrder = sortOrder++));
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),

  on(WorkflowActions.moveWorkflowStepMember, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(ws => ws.uId === action.workflowStep.uId);
      const [memberToMove] = step.teamMembers.splice(action.previousIndex, 1);
      step.teamMembers.splice(action.currentIndex, 0, memberToMove);

      let sortOrder = 0;
      step.teamMembers.map(r => (r.sortOrder = sortOrder++));
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),

  on(WorkflowActions.setUpdatedWorkflow, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflow: action.workflow,
      selectedWorkflowStep: null,
      selectedWorkflowStepRule: null,
      ui: {
        ...state.ui,
        isDirty: true
      }
    };
  }),

  on(WorkflowActions.newWorkflowComplete, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflow: action.workflow,
      selectedWorkflowStep: null,
      selectedWorkflowStepRule: null,
      ui: {
        ...state.ui,
        isDirty: true
      }
    };
  }),

  on(WorkflowActions.loadStatuses, (state, action) => {
    return {
      ...state,
      workflowStatuses: action.data
    };
  }),

  on(WorkflowActions.setTableChanged, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isTableChanged: action.changed
      }
    };
  })
);

export const getSelectedWorkflow = (state: State) => state?.selectedWorkflow;
export const getSelectedWorkflowStep = (state: State) => state?.selectedWorkflowStep;
export const getSelectedWorkflowAction = (state: State) => state?.selectedWorkflowStepAction;
export const getWorkflowStepForDelete = (state: State) => state?.selectedWorkflowStepForDelete;
export const getWorkflowStepActionForDelete = (state: State) => state?.selectedWorkflowStepActionForDelete;
export const getSelectedViewForDelete = (state: State) => state?.selectedViewForDelete;
export const getIsDirty = (state: State) => state?.ui.isDirty;
export const getIsWorking = (state: State) => state?.ui.isWorking;
export const getShowFormBuilder = (state: State) => state?.ui.showFormBuilder;
export const getDetailView = (state: State) => state?.selectedWorkflow?.views?.find(vw => vw.name === 'detail');
export const getTableView = (state: State) => state?.selectedWorkflow?.views?.find(vw => vw.name === 'table');
export const getCardView = (state: State) => state?.selectedWorkflow?.views?.find(vw => vw.name === 'card');
export const getDeleteSteps = (state: State) => state?.workflowStepsAfterDelete;
export const getIsStepMigration = (state: State) => state?.ui.isStepMigration;
export const getStepMigrationUId = (state: State) => state?.stepMigrationUId;
export const getOldStepMigrationUId = (state: State) => state?.oldStepMigrationUId;
export const getSelectedWorkflowStepRule = (state: State) => state?.selectedWorkflowStepRule;
export const getWorkflowStepRuleForDelete = (state: State) => state?.selectedWorkflowStepRuleForDelete;
export const getRuleImplementations = (state: State) => state?.ruleImplementations;
export const getTableChanged = (state: State) => state?.ui.isTableChanged;

/// Statuses
export const getWorkflowStatuses = (state: State) => state?.workflowStatuses;
